/**
 * Route helpers (non-legacy)
 */

export const routes = {
  /**
   * Reporting routes
   */
  reporting: {
    /**
     * Returns the reporting overview route
     * @returns The reporting overview route path
     */
    base: () => '/reporting',

    /**
     * Returns the new dashboard creation route
     * @returns The new dashboard creation route path
     */
    new: () => '/reporting/new',

    /**
     * Returns the route to a specific dashboard
     * @param dashboardId - The dashboard id
     * @returns The dashboard route path
     */
    dashboard: (dashboardId: string | number) => `/reporting/${dashboardId}`,
  },

  /**
   * Project routes
   */
  projects: {
    /**
     * Returns the projects portfolio page route
     * @returns The projects portfolio route path
     */
    base: () => '/projects',

    /**
     * Returns the base route to a specific project
     * @param projectId - The project's SourceId
     * @returns The base project route path
     */
    project: (projectId: string | number) => `/projects/${projectId}`,

    /**
     * Returns the route to the project planning page
     * @param projectId - The project's SourceId
     * @returns The project planning route path
     */
    planning: (projectId: string | number) => `/projects/${projectId}/planning`,

    /**
     * Returns the route to the project team page
     * @param projectId - The project's SourceId
     * @returns The project team route path
     */
    team: (projectId: string | number) => `/projects/${projectId}/team`,

    /**
     * Returns the route to the project financials page
     * @param projectId - The project's SourceId
     * @returns The project financials route path
     */
    financials: (projectId: string | number) =>
      `/projects/${projectId}/financials`,

    /**
     * Returns the route to the project billing page
     * @param projectId - The project's SourceId
     * @returns The project billing route path
     */
    billing: (projectId: string | number) => `/projects/${projectId}/billing`,

    /**
     * Returns the route to the project settings page
     * @param projectId - The project's SourceId
     * @returns The project settings route path
     */
    settings: (projectId: string | number) => `/projects/${projectId}/settings`,

    /**
     * Returns the route to a specific project tab
     * @param projectId - The project's SourceId
     * @param tab - The tab to navigate to
     * @returns The project tab route path
     */
    byTab: (
      projectId: string | number,
      tab: 'planning' | 'team' | 'financials' | 'billing' | 'settings',
    ) => `/projects/${projectId}/${tab}`,

    /**
     * Project expense routes
     */
    expenses: {
      /**
       * Returns the route to create a new project expense
       * @returns The new project expense route path
       */
      new: () => '/projects/expenses/new',
      /**
       * Returns the route to create a new expense for a specific project
       * @param projectId - The project's SourceId
       * @returns The new project expense route path
       */
      newWithId: (projectId: string | number) =>
        `/projects/${projectId}/expenses/new`,
      /**
       * Returns the route to edit a project expense
       * @param expenseId - The expense ID
       * @returns The edit project expense route path
       */
      edit: (expenseId: string | number) =>
        `/projects/expenses/${expenseId}/edit`,
    },
  },

  /**
   * Employee routes
   */
  employees: {
    /**
     * Returns the employees portfolio page route
     * @returns The employees portfolio route path
     */
    base: () => '/employees',
  },

  /**
   * Register area routes
   */
  register: {
    expenses: {
      /**
       * Returns the route to create a new register expense
       * @returns The new register expense route path
       */
      new: () => '/register/expenses/new',
      /**
       * Returns the route to edit a register expense
       * @param expenseId - The expense ID
       * @returns The edit register expense route path
       */
      edit: (expenseId: string | number) =>
        `/register/expenses/${expenseId}/edit`,
    },
  },

  /**
   * Logout route
   */
  logout: () => '/logout',

  /**
   * Development/Testing routes
   * Only available in development mode
   */
  dev: {
    /**
     * Returns the error testing page route
     * @returns The error testing route path
     */
    errorTesting: () => '/dev/error-testing',
  },
} as const;
